<template>
  <div id="contact" class="cta-final">
    <div class="landing-new__body">
      <div ref="ctaCard" class="cta-final__card">
        <div
          ref="ctaBackdrop"
          class="cta-final__backdrop"
          :class="{ 'cta-final__backdrop--animated': meshGradient }"
          aria-hidden="true"
        >
          <div ref="meshGradient" class="cta-final__mesh"></div>
        </div>
        <section class="section-block">
          <div class="section-block__head">
            <h2>{{ cta.title }}</h2>
            <p>{{ cta.description }}</p>
            <a v-if="cta.href" class="nav-bar__demo" :href="cta.href">
              {{ cta.ctaLabel }}
            </a>
            <button
              v-else
              type="button"
              class="nav-bar__demo"
              :data-tf-popup="dynamicDemoUrl"
              :data-tf-iframe-props="`title=${cta.ctaLabel || $t('home.cta.ctaLabel')}`"
              @click="tryClick"
            >
              {{ cta.ctaLabel || $t('home.cta.ctaLabel') }}
            </button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import { AmplitudeEvent } from 'components/shared/amplitude.js';

const MAX_MESH_PIXEL_COUNT = 800 * 400;
const CTA_BACKDROP_MIN_SCALE = 0.7;
const CTA_BACKDROP_END_VIEWPORT_RATIO = 0.35;
const CTA_BACKDROP_MEDIA_QUERY = '(min-width: 1212px) and (prefers-reduced-motion: no-preference)';
let meshShaderModulePromise = null;

const loadMeshShaderModule = () => {
  meshShaderModulePromise ??= import('components/libs/paper-shaders/index.js');
  return meshShaderModulePromise;
};

export default {
  props: {
    content: {
      type: Object,
      default: null,
    },
    meshColors: {
      type: Array,
      default: () => ['#2A8AEA', '#73B7ED', '#B8DCF8', '#F2F8FF'],
    },
  },
  data() {
    return {
      meshGradient: null,
      meshGradientDestroyed: false,
      meshGradientIdleCallback: null,
      meshGradientObserver: null,
      meshGradientPreloadTimer: null,
      ctaBackdropFrame: null,
      ctaBackdropLayoutObserver: null,
      ctaBackdropMediaQuery: null,
      ctaBackdropMediaQueryHandler: null,
      ctaBackdropMotionActive: false,
      ctaBackdropObserver: null,
      ctaBackdropResizeHandler: null,
      ctaBackdropScrollHandler: null,
      ctaBackdropScrollListening: false,
      ctaBackdropScale: null,
      ctaBackdropIsNearViewport: false,
    };
  },
  computed: {
    cta() {
      return (
        this.content || {
          title: this.$t('home.cta.title'),
          description: this.$t('home.cta.description'),
          ctaLabel: this.$t('home.cta.ctaLabel'),
        }
      );
    },
    dynamicDemoUrl() {
      if (this.$i18n.locale == 'pl') {
        return 'iBhXNckk';
      } else {
        return 'CctmLklr';
      }
    },
  },
  mounted() {
    this.setupCtaBackdropMotion();

    if (!this.canUseMeshGradient()) return;

    this.preloadMeshGradientWhenIdle();

    const startMeshGradient = () => {
      this.meshGradientObserver?.disconnect();
      this.meshGradientObserver = null;
      this.startMeshGradient();
    };

    if (!window.IntersectionObserver) {
      startMeshGradient();
      return;
    }

    this.meshGradientObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) startMeshGradient();
      },
      { rootMargin: '1200px 0px' }
    );
    this.meshGradientObserver.observe(this.$refs.meshGradient);
  },
  beforeUnmount() {
    this.teardownCtaBackdropMotion();
    this.meshGradientDestroyed = true;
    if (this.meshGradientIdleCallback) {
      window.cancelIdleCallback?.(this.meshGradientIdleCallback);
    }
    if (this.meshGradientPreloadTimer) {
      window.clearTimeout(this.meshGradientPreloadTimer);
    }
    this.meshGradientObserver?.disconnect();
    this.meshGradient?.dispose();
  },
  methods: {
    setupCtaBackdropMotion() {
      if (!window.matchMedia || !this.$refs.ctaBackdrop) return;

      this.ctaBackdropMediaQuery = window.matchMedia(CTA_BACKDROP_MEDIA_QUERY);
      this.ctaBackdropMediaQueryHandler = this.handleCtaBackdropMediaChange;

      if (this.ctaBackdropMediaQuery.addEventListener) {
        this.ctaBackdropMediaQuery.addEventListener('change', this.ctaBackdropMediaQueryHandler);
      } else {
        this.ctaBackdropMediaQuery.addListener(this.ctaBackdropMediaQueryHandler);
      }

      this.handleCtaBackdropMediaChange();
    },
    teardownCtaBackdropMotion() {
      this.stopCtaBackdropMotion();

      if (!this.ctaBackdropMediaQuery || !this.ctaBackdropMediaQueryHandler) return;

      if (this.ctaBackdropMediaQuery.removeEventListener) {
        this.ctaBackdropMediaQuery.removeEventListener('change', this.ctaBackdropMediaQueryHandler);
      } else {
        this.ctaBackdropMediaQuery.removeListener(this.ctaBackdropMediaQueryHandler);
      }

      this.ctaBackdropMediaQuery = null;
      this.ctaBackdropMediaQueryHandler = null;
    },
    handleCtaBackdropMediaChange() {
      if (this.ctaBackdropMediaQuery?.matches) {
        this.startCtaBackdropMotion();
      } else {
        this.stopCtaBackdropMotion();
      }
    },
    startCtaBackdropMotion() {
      if (this.ctaBackdropMotionActive || !this.$refs.ctaCard) return;

      this.ctaBackdropMotionActive = true;
      this.ctaBackdropScrollHandler = () => this.scheduleCtaBackdropUpdate();
      this.ctaBackdropResizeHandler = () => this.scheduleCtaBackdropUpdate();

      window.addEventListener('resize', this.ctaBackdropResizeHandler, { passive: true });
      window.addEventListener('load', this.ctaBackdropResizeHandler, { once: true });

      if (window.ResizeObserver) {
        this.ctaBackdropLayoutObserver = new window.ResizeObserver(this.ctaBackdropResizeHandler);
        this.ctaBackdropLayoutObserver.observe(this.$el.parentElement || this.$refs.ctaCard);
      }

      this.$refs.ctaBackdrop?.classList.add('cta-final__backdrop--motion-enabled');
      this.updateCtaBackdropTransform();

      if (!window.IntersectionObserver) {
        this.ctaBackdropIsNearViewport = true;
        this.setCtaBackdropScrollLayer(true);
        this.addCtaBackdropScrollListener();
        this.scheduleCtaBackdropUpdate();
        return;
      }

      this.ctaBackdropObserver = new IntersectionObserver(
        (entries) => this.handleCtaBackdropIntersection(entries),
        { rootMargin: '400px 0px' }
      );
      this.ctaBackdropObserver.observe(this.$refs.ctaCard);
    },
    stopCtaBackdropMotion() {
      if (this.ctaBackdropFrame) {
        window.cancelAnimationFrame(this.ctaBackdropFrame);
        this.ctaBackdropFrame = null;
      }

      this.ctaBackdropObserver?.disconnect();
      this.ctaBackdropObserver = null;
      this.ctaBackdropLayoutObserver?.disconnect();
      this.ctaBackdropLayoutObserver = null;
      this.removeCtaBackdropScrollListener();

      if (this.ctaBackdropResizeHandler) {
        window.removeEventListener('resize', this.ctaBackdropResizeHandler);
        window.removeEventListener('load', this.ctaBackdropResizeHandler);
      }

      this.ctaBackdropMotionActive = false;
      this.ctaBackdropIsNearViewport = false;
      this.ctaBackdropScrollHandler = null;
      this.ctaBackdropScale = null;
      this.ctaBackdropResizeHandler = null;
      this.$refs.ctaBackdrop?.classList.remove('cta-final__backdrop--motion-enabled');
      this.setCtaBackdropScrollLayer(false);
      this.$refs.ctaBackdrop?.style.removeProperty('transform');
    },
    handleCtaBackdropIntersection([entry]) {
      if (!this.ctaBackdropMotionActive) return;

      this.ctaBackdropIsNearViewport = entry?.isIntersecting ?? true;

      if (this.ctaBackdropIsNearViewport) {
        this.setCtaBackdropScrollLayer(true);
        this.addCtaBackdropScrollListener();
        this.scheduleCtaBackdropUpdate();
        return;
      }

      this.removeCtaBackdropScrollListener();
      this.setCtaBackdropScrollLayer(false);
      this.updateCtaBackdropTransform();
    },
    addCtaBackdropScrollListener() {
      if (this.ctaBackdropScrollListening || !this.ctaBackdropScrollHandler) return;

      window.addEventListener('scroll', this.ctaBackdropScrollHandler, { passive: true });
      this.ctaBackdropScrollListening = true;
    },
    removeCtaBackdropScrollListener() {
      if (!this.ctaBackdropScrollListening || !this.ctaBackdropScrollHandler) return;

      window.removeEventListener('scroll', this.ctaBackdropScrollHandler);
      this.ctaBackdropScrollListening = false;
    },
    setCtaBackdropScrollLayer(isActive) {
      this.$refs.ctaBackdrop?.classList.toggle('cta-final__backdrop--scrolling', isActive);
    },
    scheduleCtaBackdropUpdate() {
      if (
        !this.ctaBackdropMotionActive ||
        !this.ctaBackdropIsNearViewport ||
        this.ctaBackdropFrame
      ) {
        return;
      }

      this.ctaBackdropFrame = window.requestAnimationFrame(() => {
        this.ctaBackdropFrame = null;
        this.updateCtaBackdropTransform();
      });
    },
    updateCtaBackdropTransform() {
      const backdrop = this.$refs.ctaBackdrop;
      const card = this.$refs.ctaCard;
      const viewportHeight = window.innerHeight;

      if (!backdrop || !card || !viewportHeight) return;

      const scrollRange = viewportHeight * (1 - CTA_BACKDROP_END_VIEWPORT_RATIO);
      if (scrollRange <= 0) return;

      const progress = Math.min(
        1,
        Math.max(0, (viewportHeight - card.getBoundingClientRect().top) / scrollRange)
      );
      const targetScale = Number(
        (CTA_BACKDROP_MIN_SCALE + (1 - CTA_BACKDROP_MIN_SCALE) * progress).toFixed(4)
      );
      const scale = Math.max(this.ctaBackdropScale ?? CTA_BACKDROP_MIN_SCALE, targetScale);

      if (scale === this.ctaBackdropScale) return;

      this.ctaBackdropScale = scale;
      backdrop.style.transform = `scale(${scale})`;
    },
    canUseMeshGradient() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;

      const connection =
        navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const hasVeryLowMemory = navigator.deviceMemory && navigator.deviceMemory <= 2;
      const hasVeryLowCpu = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2;
      const hasSlowConnection =
        connection?.saveData || ['slow-2g', '2g'].includes(connection?.effectiveType);

      return !hasVeryLowMemory && !hasVeryLowCpu && !hasSlowConnection;
    },
    getMeshPixelCount() {
      const hasLimitedMemory = navigator.deviceMemory && navigator.deviceMemory <= 4;
      const hasLimitedCpu = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;

      return hasLimitedMemory || hasLimitedCpu ? 200000 : MAX_MESH_PIXEL_COUNT;
    },
    preloadMeshGradientWhenIdle() {
      const preload = () => {
        this.meshGradientIdleCallback = null;
        this.meshGradientPreloadTimer = null;
        loadMeshShaderModule().catch(() => {});
      };

      if (window.requestIdleCallback) {
        this.meshGradientIdleCallback = window.requestIdleCallback(preload, { timeout: 5000 });
      } else {
        this.meshGradientPreloadTimer = window.setTimeout(preload, 1500);
      }
    },
    async startMeshGradient() {
      try {
        const {
          getShaderColorFromString,
          meshGradientFragmentShader,
          ShaderFitOptions,
          ShaderMount,
        } = await loadMeshShaderModule();
        if (this.meshGradientDestroyed || !this.$refs.meshGradient) return;

        this.meshGradient = new ShaderMount(
          this.$refs.meshGradient,
          meshGradientFragmentShader,
          {
            u_colors: this.meshColors.map(getShaderColorFromString),
            u_colorsCount: this.meshColors.length,
            u_distortion: 0.75,
            u_swirl: 0.07,
            u_grainMixer: 0,
            u_grainOverlay: 0,
            u_fit: ShaderFitOptions.contain,
            u_rotation: 0,
            u_scale: 1,
            u_offsetX: 0,
            u_offsetY: 0,
            u_originX: 0.5,
            u_originY: 0.5,
            u_worldWidth: 0,
            u_worldHeight: 0,
          },
          undefined,
          1,
          0,
          1,
          this.getMeshPixelCount()
        );
      } catch {
        // The CSS background remains visible when WebGL is unavailable.
      }
    },
    tryClick() {
      new AmplitudeEvent('Try', { spot: 'bottom' });
    },
  },
};
</script>

<style lang="scss" scoped>
@use './demo-button' as *;

.cta-final {
  background: var(--cta-background, #ffffff);
  padding: 0;
  text-align: center;
}

.cta-final__card {
  border-radius: 24px;
  contain: paint;
  isolation: isolate;
  padding: 24px 48px;
  position: relative;
}

.cta-final__backdrop {
  background: var(
    --cta-backdrop,
    linear-gradient(135deg, rgba(42, 138, 234, 0.15) 0%, rgba(42, 138, 234, 0.04) 100%)
  );
  border-radius: inherit;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  transform: scale(1);
  transform-origin: center;
  z-index: 0;
}

.cta-final__backdrop--animated::before {
  background: var(--cta-overlay, rgba(255, 255, 255, 0.48));
  content: '';
  inset: 0;
  pointer-events: none;
  position: absolute;
  z-index: 1;
}

.cta-final__mesh {
  inset: 0;
  pointer-events: none;
  position: absolute;
  z-index: 0;

  :deep(canvas) {
    display: block;
    height: 100%;
    width: 100%;
  }
}

@media (min-width: 1212px) and (prefers-reduced-motion: no-preference) {
  .cta-final__backdrop--motion-enabled {
    transform: scale(0.7);
  }

  .cta-final__backdrop--scrolling {
    will-change: transform;
  }
}

.section-block {
  padding: 80px 0;
  position: relative;
  z-index: 2;
}

.section-block__head {
  h2 {
    color: var(--cta-heading, #1b2733);
    font-size: 31px;
    font-weight: 700;
    line-height: 1.25;
  }

  p {
    color: var(--cta-description, rgba(27, 39, 51, 0.7));
    font-size: 14px;
    line-height: 1.6;
    margin: 12px auto 12px;
    max-width: 680px;
  }
}

.nav-bar__demo {
  @include demo-button;
  appearance: none;
  background: #2a8aea;
  color: #fff;
  margin-top: 28px;
  display: inline-flex;
  align-items: center;

  &:hover {
    background: #1a7dd4;
    box-shadow: 0 4px 14px rgba(42, 138, 234, 0.3);
  }

  &:focus-visible {
    outline: 3px solid #1b2733;
    outline-offset: 3px;
  }
}

@media (max-width: 1024px) {
  .section-block {
    padding: 60px 0;
  }
}

@media (max-width: 767px) {
  .cta-final__card {
    padding: 32px 24px;
    border-radius: 16px;
  }
}

@media (max-width: 413px) {
  .section-block__head h2 {
    font-size: 27px;
  }
}
</style>
